// ahri_v5 — domain/featured.js

import { normalizeWarehouseCode } from "./availability.js";

export function normalizeFeaturedGroupTag(value) {
  return String(value || "").trim().replace(/\s+/g, " ").toUpperCase();
}

export function isAllWarehouseTag(value) {
  const normalized = normalizeFeaturedGroupTag(value);
  return normalized === "ALL" || normalized === "ALL WAREHOUSE" || normalized === "ALL WAREHOUSES";
}

// Branch codes get stored/typed inconsistently (zero-padded "073" vs bare
// "73"). Mirrors the backend's _warehouse_code_candidates so a selected
// warehouse matches a branch_codes entry regardless of which form either
// side used.
function warehouseCodeCandidates(value) {
  const raw = normalizeFeaturedGroupTag(value);
  const candidates = new Set();
  if (!raw) return candidates;
  candidates.add(raw);
  if (/^\d+$/.test(raw)) {
    candidates.add(raw.padStart(3, "0"));
    candidates.add(raw.replace(/^0+/, "") || "0");
  }
  return candidates;
}

export function isFeaturedForWarehouse(system, selectedWarehouseCode) {
  if (!system?.validFeatured) return false;
  const normalizedWarehouse = normalizeFeaturedGroupTag(normalizeWarehouseCode(selectedWarehouseCode));
  // No branch context selected -- show general featured status.
  if (!normalizedWarehouse) return true;
  const normalizedTags = Array.from(
    new Set(
      (Array.isArray(system?.featuredBranchCodes) ? system.featuredBranchCodes : [])
        .map((entry) => normalizeFeaturedGroupTag(entry))
        .filter(Boolean)
    )
  );
  // Featured-for-this-branch requires a matching branch code (or "ALL") in
  // featuredBranchCodes -- no match means not featured for this branch.
  if (normalizedTags.some((tag) => isAllWarehouseTag(tag))) return true;
  const warehouseCandidates = warehouseCodeCandidates(normalizedWarehouse);
  return normalizedTags.some((tag) => warehouseCandidates.has(tag));
}

export function getFeaturedGroupNamesForWarehouse(system, selectedWarehouseCode) {
  if (!system?.validFeatured) return [];
  const memberships = Array.isArray(system?.featuredMemberships) ? system.featuredMemberships : [];
  const normalizedWarehouse = normalizeFeaturedGroupTag(normalizeWarehouseCode(selectedWarehouseCode));
  if (!memberships.length) {
    // No per-group detail available (e.g. payload_json missing) -- fall back
    // to whatever single name was resolved from the flattened union fields.
    return system?.featuredGroupName ? [system.featuredGroupName] : [];
  }
  const warehouseCandidates = warehouseCodeCandidates(normalizedWarehouse);
  const matchesWarehouse = (membership) => {
    if (!normalizedWarehouse) return true; // no branch context -- every group applies
    const tags = membership.branchCodes.map((code) => normalizeFeaturedGroupTag(code));
    if (tags.some((tag) => isAllWarehouseTag(tag))) return true;
    return tags.some((tag) => warehouseCandidates.has(tag));
  };
  const names = [];
  const seen = new Set();
  for (const membership of memberships) {
    if (!membership.groupName || !matchesWarehouse(membership)) continue;
    const key = membership.groupName.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(membership.groupName);
  }
  return names;
}

export function compareSystemsForDisplay(a, b) {
  if (a.featuredForWarehouse !== b.featuredForWarehouse) {
    return a.featuredForWarehouse ? -1 : 1;
  }
  if (a.availabilityBucketRank !== b.availabilityBucketRank) {
    return a.availabilityBucketRank - b.availabilityBucketRank;
  }
  if (a.completeMatch !== b.completeMatch) return a.completeMatch ? -1 : 1;
  if (a.partialMatch !== b.partialMatch) return a.partialMatch ? -1 : 1;
  if (a.sortScore !== b.sortScore) return b.sortScore - a.sortScore;
  const brandOrder = String(a.outdoorBrand || "").localeCompare(String(b.outdoorBrand || ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
  if (brandOrder !== 0) return brandOrder;
  return (a._sourceIndex || 0) - (b._sourceIndex || 0);
}
